import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '@src/api/clients';
import { DateRangePicker } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

const DAY_MS = 24 * 60 * 60 * 1000;
const NUMERIC_KINDS = ['money', 'int', 'percent'];
const isNumericKind = (kind) => NUMERIC_KINDS.includes(kind);
const OP_SYMBOL = { '*': '×', '/': '÷' }; // display only; value stays * /

function defaultRange() {
  const end = new Date(Date.now() - DAY_MS); // yesterday (last complete day)
  const start = new Date(end.getTime() - 29 * DAY_MS);
  return { start, end };
}

function formatCell(value, kind) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value !== 'number') return String(value);
  if (!Number.isFinite(value)) return '—';
  if (kind === 'percent') return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  if (kind === 'money') {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return value.toLocaleString();
}

function errorText(err, fallback) {
  return err?.response?.data?.detail || err?.data?.detail || err?.message || fallback;
}

// Sticky cell styling — header stays on top, the first (breakdown) column stays
// on the left, and the totals row stays at the bottom while scrolling.
// Header/totals use an opaque token so scrolled cells don't bleed through.
const HEADER_BG = 'background.level1';
const headCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: HEADER_BG,
};
const firstHeadCellSx = {
  ...headCellSx,
  position: 'sticky',
  left: 0,
  zIndex: 4,
  borderRight: '1px solid',
  borderRightColor: 'divider',
};
const firstBodyCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
  borderRight: '1px solid',
  borderRightColor: 'divider',
  whiteSpace: 'nowrap',
};
const totalsRowSx = {
  '& td': {
    fontWeight: 700,
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    bgcolor: HEADER_BG,
    borderTop: '2px solid',
    borderTopColor: 'divider',
  },
};
const firstTotalsCellSx = {
  position: 'sticky',
  left: 0,
  bottom: 0,
  zIndex: 3,
  borderRight: '1px solid',
  borderRightColor: 'divider',
};
const compactCellSx = { py: 0.75, px: 1.5 };

export default function ReportBuilderPage() {
  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState('');

  const { options: clientOptions, loading: clientsLoading, handleInputChange } =
    useClientAutocomplete();

  const [selectedClient, setSelectedClient] = useState(null);
  const clientId = selectedClient?.value || '';
  const [reportName, setReportName] = useState('');
  const [dimension, setDimension] = useState('vertical');
  const [timeGrain, setTimeGrain] = useState('none');
  const [metrics, setMetrics] = useState([]); // ordered list of selected metric keys
  const [format, setFormat] = useState('xlsx');
  const [tab, setTab] = useState(0);
  const [metricSearch, setMetricSearch] = useState('');

  // Date mode: fixed (picker) vs rolling (last N complete days, stays current).
  const [dateMode, setDateMode] = useState('fixed');
  const [rollingDays, setRollingDays] = useState(30);

  // Saved reports (Phase 2).
  const [savedReports, setSavedReports] = useState([]);
  const [loadedDefId, setLoadedDefId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [saveError, setSaveError] = useState('');

  const initialRange = useMemo(defaultRange, []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [exportError, setExportError] = useState('');

  const [sortState, setSortState] = useState(null); // { key, dir }
  const [dragOverKey, setDragOverKey] = useState(null);
  const dragKey = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Fee toggle — spend (and every spend-based metric) includes agency fees by
  // default; flip off to view raw platform spend across the whole report.
  const [includeFees, setIncludeFees] = useState(true);

  // Custom-metric builder (chip/token based — no field typing).
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customTokens, setCustomTokens] = useState([]); // {type,value,label}
  const [numDraft, setNumDraft] = useState('');
  const [customKind, setCustomKind] = useState('number');
  const [customError, setCustomError] = useState('');
  const [customSaving, setCustomSaving] = useState(false);
  const fieldDragRef = useRef(null);

  // -- load catalog --------------------------------------------------------
  const fetchMetadata = async ({ initial = false } = {}) => {
    try {
      const res = await api.request({ url: 'dashboard/report-builder/metadata/', method: 'GET' });
      const data = res?.data || null;
      setMetadata(data);
      if (initial && data?.default_metrics) setMetrics(data.default_metrics);
    } catch (err) {
      setMetadataError(errorText(err, 'Failed to load report options.'));
    }
  };
  useEffect(() => {
    fetchMetadata({ initial: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- saved reports -------------------------------------------------------
  const fetchSavedReports = async () => {
    try {
      const res = await api.request({ url: 'dashboard/report-definitions/', method: 'GET' });
      const data = res?.data;
      setSavedReports(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      /* non-fatal — saved list just stays empty */
    }
  };
  useEffect(() => {
    fetchSavedReports();
  }, []);

  const applyDefinition = (def) => {
    if (!def) return;
    const spec = def.spec || {};
    const f = spec.filters || {};
    setLoadedDefId(def.id);
    setReportName(def.name || '');
    setSelectedClient(
      def.client ? { value: def.client, label: def.client_name || `Client ${def.client}` } : null
    );
    if (spec.dimension) setDimension(spec.dimension);
    if (spec.time_grain) setTimeGrain(spec.time_grain);
    if (Array.isArray(spec.metrics)) setMetrics(spec.metrics);
    if (spec.format) setFormat(spec.format);
    setIncludeFees(spec.include_fees !== false);
    if (f.date_mode === 'rolling') {
      setDateMode('rolling');
      setRollingDays(f.rolling_days || 30);
    } else {
      setDateMode('fixed');
      if (f.date_after) setStartDate(new Date(`${f.date_after}T00:00:00`));
      if (f.date_before) setEndDate(new Date(`${f.date_before}T00:00:00`));
    }
    setSaveStatus('');
    setSaveError('');
  };

  const handleLoadSaved = (id) => {
    if (!id) {
      setLoadedDefId(null);
      return;
    }
    applyDefinition(savedReports.find((r) => r.id === id));
  };

  const saveReport = async (asNew = false) => {
    setSaveError('');
    setSaveStatus('');
    if (!reportName.trim()) {
      setSaveError('Name your report (in the toolbar) before saving.');
      return;
    }
    if (!clientId) {
      setSaveError('Select a client before saving.');
      return;
    }
    const payload = { name: reportName.trim(), spec: buildSpec() };
    try {
      const editing = loadedDefId && !asNew;
      const res = await api.request({
        url: editing
          ? `dashboard/report-definitions/${loadedDefId}/`
          : 'dashboard/report-definitions/',
        method: editing ? 'PATCH' : 'POST',
        payload,
      });
      setLoadedDefId(res?.data?.id || null);
      setSaveStatus(editing ? 'Report updated.' : 'Report saved.');
      fetchSavedReports();
    } catch (err) {
      setSaveError(errorText(err, 'Failed to save report.'));
    }
  };

  const deleteReport = async () => {
    if (!loadedDefId) return;
    try {
      await api.request({ url: `dashboard/report-definitions/${loadedDefId}/`, method: 'DELETE' });
      setLoadedDefId(null);
      setSaveStatus('Report deleted.');
      fetchSavedReports();
    } catch (err) {
      setSaveError(errorText(err, 'Failed to delete report.'));
    }
  };

  const validMetricKeys = useMemo(() => {
    const dim = metadata?.dimensions?.find((d) => d.key === dimension);
    return new Set(dim?.valid_metrics || []);
  }, [metadata, dimension]);

  // Drop metrics that aren't valid for the chosen breakdown.
  useEffect(() => {
    if (!metadata) return;
    setMetrics((prev) => prev.filter((key) => validMetricKeys.has(key)));
  }, [metadata, dimension]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preview re-fetches when the metric SET changes (not its order — reordering
  // is handled client-side so dragging columns doesn't hit the network).
  const metricSetKey = useMemo(() => [...metrics].sort().join(','), [metrics]);

  const buildFilters = () =>
    dateMode === 'rolling'
      ? { date_mode: 'rolling', rolling_days: Number(rollingDays) || 30 }
      : {
          date_mode: 'fixed',
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
        };

  const buildSpec = () => ({
    name: reportName.trim() || undefined,
    dimension,
    time_grain: timeGrain,
    metrics,
    client_id: clientId,
    format,
    include_fees: includeFees,
    filters: buildFilters(),
  });

  // -- live, debounced, race-guarded preview -------------------------------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!clientId || metrics.length === 0) {
      setPreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    debounceRef.current = setTimeout(async () => {
      const reqId = (reqIdRef.current += 1);
      setPreviewError('');
      try {
        const res = await api.request({
          url: 'dashboard/report-builder/preview/',
          method: 'POST',
          payload: buildSpec(),
        });
        if (reqId !== reqIdRef.current) return; // a newer request superseded this
        setPreview(res?.data || null);
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setPreview(null);
        setPreviewError(errorText(err, 'Preview failed.'));
      } finally {
        if (reqId === reqIdRef.current) setPreviewLoading(false);
      }
    }, 450);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, dimension, timeGrain, metricSetKey, startDate, endDate, dateMode, rollingDays, includeFees]);

  // -- column model: structural (lead, locked) + metric (reorderable) ------
  const { structuralColumns, metricColumns } = useMemo(() => {
    const cols = preview?.columns || [];
    const structural = cols.filter((c) => !isNumericKind(c.kind));
    const metricCols = cols.filter((c) => isNumericKind(c.kind));
    // Metric columns follow the user's selected order.
    metricCols.sort((a, b) => metrics.indexOf(a.key) - metrics.indexOf(b.key));
    return { structuralColumns: structural, metricColumns: metricCols };
  }, [preview, metrics]);

  const orderedColumns = useMemo(
    () => [...structuralColumns, ...metricColumns],
    [structuralColumns, metricColumns]
  );
  const colByKey = useMemo(() => {
    const map = {};
    orderedColumns.forEach((c) => {
      map[c.key] = c;
    });
    return map;
  }, [orderedColumns]);

  // Drop a stale sort key when the column set changes.
  useEffect(() => {
    if (sortState && !colByKey[sortState.key]) setSortState(null);
  }, [colByKey, sortState]);

  const sortedRows = useMemo(() => {
    const rows = [...(preview?.rows || [])];
    const col = sortState && colByKey[sortState.key];
    if (!col) return rows;
    const numeric = isNumericKind(col.kind);
    rows.sort((a, b) => {
      let av = a[sortState.key];
      let bv = b[sortState.key];
      if (numeric) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
        return sortState.dir === 'asc' ? av - bv : bv - av;
      }
      return sortState.dir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
    return rows;
  }, [preview, sortState, colByKey]);

  const rowKeyFor = (row) => structuralColumns.map((c) => row[c.key]).join('|') || 'row';

  // -- interactions --------------------------------------------------------
  const toggleMetric = (key) => {
    setMetrics((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const selectAllMetrics = () => {
    const all = (metadata?.metrics || [])
      .map((m) => m.key)
      .filter((k) => validMetricKeys.has(k));
    setMetrics(all);
  };
  const clearMetrics = () => setMetrics([]);

  // -- custom metrics ------------------------------------------------------
  const openCustomDialog = () => {
    setCustomName('');
    setCustomTokens([]);
    setNumDraft('');
    setCustomKind('number');
    setCustomError('');
    setCustomOpen(true);
  };
  const addFieldToken = (b) =>
    setCustomTokens((p) => [...p, { type: 'field', value: b.key, label: b.label }]);
  const addOpToken = (op) =>
    setCustomTokens((p) => [...p, { type: 'op', value: op, label: OP_SYMBOL[op] || op }]);
  const addNumToken = () => {
    const n = numDraft.trim();
    if (n === '' || Number.isNaN(Number(n))) return;
    setCustomTokens((p) => [...p, { type: 'num', value: n, label: n }]);
    setNumDraft('');
  };
  const removeTokenAt = (idx) => setCustomTokens((p) => p.filter((_, i) => i !== idx));
  const customExprString = customTokens.map((t) => t.value).join(' ');

  const saveCustomMetric = async () => {
    setCustomError('');
    if (!customName.trim()) {
      setCustomError('Name your metric.');
      return;
    }
    if (!customTokens.length) {
      setCustomError('Build a formula using the fields and operators below.');
      return;
    }
    setCustomSaving(true);
    try {
      const res = await api.request({
        url: 'dashboard/report-custom-metrics/',
        method: 'POST',
        payload: { name: customName.trim(), expression: customExprString, kind: customKind },
      });
      await fetchMetadata();
      const newKey = res?.data?.id ? `custom:${res.data.id}` : null;
      if (newKey) setMetrics((prev) => (prev.includes(newKey) ? prev : [...prev, newKey]));
      setCustomOpen(false);
    } catch (err) {
      const d = err?.response?.data;
      setCustomError(
        d?.expression?.[0] || d?.name?.[0] || d?.detail || err?.message ||
        'Could not save metric — check the formula.'
      );
    } finally {
      setCustomSaving(false);
    }
  };

  const deleteCustomMetric = async (key) => {
    const id = key.split(':')[1];
    try {
      await api.request({ url: `dashboard/report-custom-metrics/${id}/`, method: 'DELETE' });
      setMetrics((prev) => prev.filter((k) => k !== key));
      await fetchMetadata();
    } catch (err) {
      /* non-fatal */
    }
  };

  const handleSort = (col) => {
    setSortState((prev) => {
      if (prev?.key === col.key) return { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { key: col.key, dir: isNumericKind(col.kind) ? 'desc' : 'asc' };
    });
  };

  const reorderMetric = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    setMetrics((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(fromKey);
      const ti = arr.indexOf(toKey);
      if (fi < 0 || ti < 0) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, fromKey);
      return arr;
    });
  };

  const handleExport = async () => {
    if (!clientId || metrics.length === 0) return;
    setExporting(true);
    setExportError('');
    setExportStatus('Preparing report…');
    try {
      const startRes = await api.request({
        url: 'dashboard/report-builder/run/',
        method: 'POST',
        payload: buildSpec(),
      });
      const runId = startRes?.data?.id;
      if (!runId) throw new Error('Failed to start report.');
      setExportStatus('Generating report…');
      let statusData = null;
      for (let attempt = 0; attempt < 180; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const statusRes = await api.request({
          url: `dashboard/report-builder/runs/${runId}/status/`,
          method: 'GET',
        });
        statusData = statusRes?.data || null;
        if (statusData?.status === 'succeeded') break;
        if (statusData?.status === 'failed') {
          throw new Error(statusData?.error_message || 'Report generation failed.');
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (!statusData || statusData.status !== 'succeeded' || !statusData.download_url) {
        throw new Error('Report timed out. Please try again.');
      }
      const downloadRes = await api.request({
        url: `dashboard/report-builder/runs/${runId}/download/`,
        method: 'GET',
        options: { responseType: 'blob' },
      });
      const disposition = downloadRes?.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : `report-${runId}.${format}`;
      const blob =
        downloadRes?.data instanceof Blob ? downloadRes.data : new Blob([downloadRes?.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportStatus('Report ready. Download started.');
    } catch (err) {
      setExportError(errorText(err, 'Failed to generate report.'));
    } finally {
      setExporting(false);
    }
  };

  const handleDateChange = ({ selection }) => {
    if (!selection) return;
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
  };

  // -- render helpers ------------------------------------------------------
  // Merge the loaded client into the options so its name shows even before the
  // autocomplete has searched for it.
  const clientAcOptions = useMemo(() => {
    if (selectedClient && !clientOptions.some((o) => o.value === selectedClient.value)) {
      return [selectedClient, ...clientOptions];
    }
    return clientOptions;
  }, [clientOptions, selectedClient]);
  const dimensions = metadata?.dimensions || [];
  const timeGrains = metadata?.time_grains || [];
  const allMetrics = metadata?.metrics || [];
  const categories = metadata?.metric_categories || [];
  const totals = preview?.totals || null;
  const canExport = Boolean(clientId) && metrics.length > 0;
  const rowCount = preview?.row_count ?? 0;

  const search = metricSearch.trim().toLowerCase();
  const metricsByCategory = useMemo(() => {
    const groups = {};
    allMetrics.forEach((m) => {
      if (search && !m.label.toLowerCase().includes(search)) return;
      const cat = m.category || 'Other';
      (groups[cat] = groups[cat] || []).push(m);
    });
    return groups;
  }, [allMetrics, search]);
  const orderedCategories = categories.filter((c) => metricsByCategory[c]?.length);

  const renderHeaderCell = (col, idx) => {
    const numeric = isNumericKind(col.kind);
    const isFirst = idx === 0;
    const isMetric = numeric; // only metric columns are reorderable
    const sx = {
      ...compactCellSx,
      ...(isFirst ? firstHeadCellSx : headCellSx),
      ...(dragOverKey === col.key ? { boxShadow: 'inset 2px 0 0 0 #1976d2' } : null),
    };
    return (
      <TableCell
        key={col.key}
        align={numeric ? 'right' : 'left'}
        sortDirection={sortState?.key === col.key ? sortState.dir : false}
        sx={sx}
        onDragOver={isMetric ? (e) => { e.preventDefault(); setDragOverKey(col.key); } : undefined}
        onDragLeave={isMetric ? () => setDragOverKey(null) : undefined}
        onDrop={
          isMetric
            ? (e) => {
                e.preventDefault();
                reorderMetric(dragKey.current, col.key);
                dragKey.current = null;
                setDragOverKey(null);
              }
            : undefined
        }
      >
        <Stack direction="row" spacing={0.25} alignItems="center" justifyContent={numeric ? 'flex-end' : 'flex-start'}>
          {isMetric ? (
            <Tooltip title="Drag to reorder">
              <DragIndicatorIcon
                fontSize="inherit"
                draggable
                onDragStart={() => { dragKey.current = col.key; }}
                onDragEnd={() => { dragKey.current = null; setDragOverKey(null); }}
                sx={{ cursor: 'grab', color: 'text.disabled', fontSize: 16 }}
              />
            </Tooltip>
          ) : null}
          <TableSortLabel
            active={sortState?.key === col.key}
            direction={sortState?.key === col.key ? sortState.dir : 'asc'}
            onClick={() => handleSort(col)}
          >
            {col.label}
          </TableSortLabel>
        </Stack>
      </TableCell>
    );
  };

  const renderGrid = () => {
    if (!clientId) {
      return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>Select a client to begin</Typography>
          <Typography variant="body2">Choose a client above, then pick a breakdown and metrics on the right.</Typography>
        </Paper>
      );
    }
    if (metrics.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>No metrics selected</Typography>
          <Typography variant="body2">
            Open the <Link component="button" type="button" onClick={() => setTab(1)}>Metrics</Link> tab and check at least one metric to preview data.
          </Typography>
        </Paper>
      );
    }
    if (preview && rowCount === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>No data for this selection</Typography>
          <Typography variant="body2">
            This client has no <strong>{colByKey && dimensions.find((d) => d.key === dimension)?.label?.toLowerCase()}</strong> data
            in {preview.window?.date_after} – {preview.window?.date_before}. Try a different breakdown, a wider date range, or another client.
          </Typography>
        </Paper>
      );
    }
    if (!preview || orderedColumns.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Building preview…</Typography>
        </Paper>
      );
    }
    return (
      <>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            {rowCount} row{rowCount === 1 ? '' : 's'}
            {preview.window ? ` · ${preview.window.date_after} → ${preview.window.date_before}` : ''}
          </Typography>
          {preview.truncated ? (
            <Typography variant="caption" color="warning.main">· preview capped — export for the full set</Typography>
          ) : null}
        </Stack>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: { xs: 360, md: 600 }, overflow: 'auto' }}
        >
          <Table size="small" stickyHeader sx={{ '& td, & th': compactCellSx }}>
            <TableHead>
              <TableRow>{orderedColumns.map((col, idx) => renderHeaderCell(col, idx))}</TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={rowKeyFor(row)} hover>
                  {orderedColumns.map((col, idx) => (
                    <TableCell
                      key={col.key}
                      align={isNumericKind(col.kind) ? 'right' : 'left'}
                      sx={idx === 0 ? firstBodyCellSx : undefined}
                    >
                      {formatCell(row[col.key], col.kind)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {totals && rowCount > 0 ? (
                <TableRow sx={totalsRowSx}>
                  {orderedColumns.map((col, idx) => (
                    <TableCell
                      key={col.key}
                      align={isNumericKind(col.kind) ? 'right' : 'left'}
                      sx={idx === 0 ? firstTotalsCellSx : undefined}
                    >
                      {col.key in totals ? formatCell(totals[col.key], col.kind) : idx === 0 ? 'Total' : ''}
                    </TableCell>
                  ))}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Card>
      <CardHeader
        title="Custom Report Builder"
        subheader="Pick a breakdown, time grain, and metrics — the report updates live. Drag the ⠿ handle to reorder metric columns, click a header to sort, then export."
      />
      <Divider />
      <CardContent>
        {metadataError ? <Alert severity="error" sx={{ mb: 2 }}>{metadataError}</Alert> : null}

        {/* Toolbar */}
        <Box
          sx={(t) => ({
            border: `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            p: 2,
            mb: 2,
            bgcolor: t.palette.background.level1 || t.palette.background.default,
          })}
        >
          {/* Saved reports row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Saved reports</InputLabel>
              <Select
                label="Saved reports"
                value={loadedDefId || ''}
                onChange={(e) => handleLoadSaved(e.target.value)}
              >
                <MenuItem value=""><em>New report…</em></MenuItem>
                {savedReports.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => saveReport(false)}>
              {loadedDefId ? 'Update' : 'Save'}
            </Button>
            {loadedDefId ? (
              <Button variant="text" onClick={() => saveReport(true)}>Save as new</Button>
            ) : null}
            {loadedDefId ? (
              <Button variant="text" color="error" onClick={deleteReport}>Delete</Button>
            ) : null}
            {saveStatus ? <Typography variant="caption" color="success.main">{saveStatus}</Typography> : null}
            {saveError ? <Typography variant="caption" color="error">{saveError}</Typography> : null}
          </Stack>
          <Divider sx={{ mb: 1.5 }} />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              options={clientAcOptions}
              loading={clientsLoading}
              value={selectedClient}
              onChange={(_e, opt) => setSelectedClient(opt || null)}
              onInputChange={(_e, val) => handleInputChange?.(val)}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              renderInput={(params) => <TextField {...params} label="Client (required)" />}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Date range</InputLabel>
              <Select label="Date range" value={dateMode} onChange={(e) => setDateMode(e.target.value)}>
                <MenuItem value="fixed">Fixed dates</MenuItem>
                <MenuItem value="rolling">Rolling</MenuItem>
              </Select>
            </FormControl>
            {dateMode === 'fixed' ? (
              <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateChange} />
            ) : (
              <TextField
                size="small"
                type="number"
                label="Last N days"
                value={rollingDays}
                onChange={(e) => setRollingDays(e.target.value)}
                inputProps={{ min: 1, max: 730 }}
                sx={{ width: 130 }}
              />
            )}
            <TextField
              size="small"
              label="Report name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Format</InputLabel>
              <Select value={format} label="Format" onChange={(e) => setFormat(e.target.value)}>
                <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Include agency fees in spend & all spend-based metrics (Cost/Lead, Cost of Marketing, etc.)">
              <FormControlLabel
                control={<Switch size="small" checked={includeFees} onChange={(e) => setIncludeFees(e.target.checked)} />}
                label={<Typography variant="body2">Include fees</Typography>}
              />
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={canExport ? '' : 'Select a client and at least one metric'}>
              <span>
                <Button variant="contained" onClick={handleExport} disabled={!canExport || exporting} sx={{ minWidth: 120 }}>
                  {exporting ? 'Exporting…' : 'Export'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {exportStatus || exportError ? (
            <Typography variant="caption" color={exportError ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
              {exportError || exportStatus}
            </Typography>
          ) : null}
        </Box>

        {previewError ? <Alert severity="error" sx={{ mb: 1.5 }}>{previewError}</Alert> : null}
        {preview ? (
          <Alert severity={preview.truncated ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
            Data current through <strong>{preview.data_through || '—'}</strong>. Summary data refreshes
            nightly and the most recent day(s) may still be settling, so very recent numbers can change.
          </Alert>
        ) : null}

        {/* Builder: live grid + customize panel */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            <Box sx={{ height: 4, mb: 0.75 }}>{previewLoading ? <LinearProgress /> : null}</Box>
            {renderGrid()}
          </Box>

          {/* Customize panel */}
          <Paper variant="outlined" sx={{ width: { xs: '100%', md: 330 }, flexShrink: 0, alignSelf: 'stretch' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>Customize</Typography>
            </Box>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 42 }}>
              <Tab label="Breakdowns" sx={{ minHeight: 42 }} />
              <Tab label={`Metrics (${metrics.length})`} sx={{ minHeight: 42 }} />
            </Tabs>
            <Divider />
            <Box sx={{ p: 2, maxHeight: { xs: 420, md: 600 }, overflowY: 'auto' }}>
              {tab === 0 ? (
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Break rows by</FormLabel>
                    <RadioGroup value={dimension} onChange={(e) => setDimension(e.target.value)}>
                      {dimensions.map((d) => (
                        <FormControlLabel
                          key={d.key}
                          value={d.key}
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">{d.label}</Typography>}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <Divider />
                  <FormControl>
                    <FormLabel sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Time</FormLabel>
                    <RadioGroup value={timeGrain} onChange={(e) => setTimeGrain(e.target.value)}>
                      {timeGrains.map((g) => (
                        <FormControlLabel
                          key={g.key}
                          value={g.key}
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">{g.label}</Typography>}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search metrics…"
                    value={metricSearch}
                    onChange={(e) => setMetricSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" onClick={selectAllMetrics}>Select all</Button>
                    <Button size="small" color="inherit" onClick={clearMetrics}>Clear</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">{metrics.length} selected</Typography>
                  </Stack>
                  <Button size="small" startIcon={<AddIcon />} onClick={openCustomDialog} sx={{ alignSelf: 'flex-start' }}>
                    New custom metric
                  </Button>
                  <Divider />
                  {orderedCategories.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No metrics match “{metricSearch}”.</Typography>
                  ) : (
                    orderedCategories.map((cat) => (
                      <Box key={cat}>
                        <Typography variant="overline" color="text.secondary">{cat}</Typography>
                        <Stack spacing={0.25}>
                          {metricsByCategory[cat].map((m) => {
                            const enabled = validMetricKeys.has(m.key);
                            const isCustom = m.key.startsWith('custom:');
                            const control = (
                              <FormControlLabel
                                disabled={!enabled}
                                sx={{ mr: 0, flexGrow: 1 }}
                                control={
                                  <Checkbox size="small" checked={metrics.includes(m.key)} onChange={() => toggleMetric(m.key)} />
                                }
                                label={
                                  <Typography variant="body2" color={enabled ? 'text.primary' : 'text.disabled'}>
                                    {m.label}
                                  </Typography>
                                }
                              />
                            );
                            return (
                              <Stack key={m.key} direction="row" alignItems="center">
                                {enabled ? (
                                  control
                                ) : (
                                  <Tooltip title="Not available for this breakdown (no spend at this grain)" placement="left">
                                    <span style={{ flexGrow: 1 }}>{control}</span>
                                  </Tooltip>
                                )}
                                {isCustom ? (
                                  <Tooltip title="Delete custom metric">
                                    <IconButton size="small" onClick={() => deleteCustomMetric(m.key)}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))
                  )}
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </CardContent>

      <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New custom metric</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Metric name"
              size="small"
              fullWidth
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <InputLabel>Format</InputLabel>
              <Select label="Format" value={customKind} onChange={(e) => setCustomKind(e.target.value)}>
                <MenuItem value="money">Currency ($)</MenuItem>
                <MenuItem value="percent">Percent (%)</MenuItem>
                <MenuItem value="number">Number</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Formula</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" color="inherit" disabled={!customTokens.length}
                  onClick={() => setCustomTokens((p) => p.slice(0, -1))}>⌫ Back</Button>
                <Button size="small" color="inherit" disabled={!customTokens.length}
                  onClick={() => setCustomTokens([])}>Clear</Button>
              </Stack>
              {/* Drop zone — drag fields here, or click chips below */}
              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (fieldDragRef.current) {
                    addFieldToken(fieldDragRef.current);
                    fieldDragRef.current = null;
                  }
                }}
                sx={{
                  minHeight: 56, p: 1, borderRadius: 1, display: 'flex',
                  flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
                  border: '1px dashed', borderColor: 'divider', bgcolor: 'background.level1',
                }}
              >
                {customTokens.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    Drag a field here, or click the chips below to build your formula.
                  </Typography>
                ) : (
                  customTokens.map((t, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      label={t.label}
                      onDelete={() => removeTokenAt(idx)}
                      color={t.type === 'field' ? 'primary' : 'default'}
                      variant={t.type === 'op' ? 'outlined' : 'filled'}
                    />
                  ))
                )}
              </Box>
              {customExprString ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                  {customExprString}
                </Typography>
              ) : null}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Fields — click or drag
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {(metadata?.building_blocks || []).map((b) => (
                  <Chip
                    key={b.key}
                    size="small"
                    label={b.label}
                    variant="outlined"
                    clickable
                    draggable
                    onDragStart={() => { fieldDragRef.current = b; }}
                    onDragEnd={() => { fieldDragRef.current = null; }}
                    onClick={() => addFieldToken(b)}
                    sx={{ cursor: 'grab' }}
                  />
                ))}
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {['+', '-', '*', '/', '(', ')'].map((op) => (
                  <Button key={op} size="small" variant="outlined" onClick={() => addOpToken(op)} sx={{ minWidth: 40, px: 0 }}>
                    {OP_SYMBOL[op] || op}
                  </Button>
                ))}
                <TextField
                  size="small"
                  type="number"
                  placeholder="number"
                  value={numDraft}
                  onChange={(e) => setNumDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNumToken(); } }}
                  sx={{ width: 110 }}
                />
                <Button size="small" variant="outlined" onClick={addNumToken} disabled={numDraft.trim() === ''}>
                  Add #
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Percent format multiplies the result ×100. “Spend” follows the report's fee toggle.
              </Typography>
            </Box>
            {customError ? <Alert severity="error">{customError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCustomMetric} disabled={customSaving}>
            {customSaving ? 'Saving…' : 'Save metric'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
